import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shadcnComponents/ui/card";
import { Progress } from "@/shadcnComponents/ui/progress";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Calendar,
  CheckCircle2,
  ExternalLink,
  Gift,
  PlayCircle,
  Star,
  Trophy,
  Users,
  Video,
  Sparkles,
  Coins,
} from "lucide-react";
import { Badge } from "@/shadcnComponents/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcnComponents/ui/accordion";
import { useCallback, useEffect, useState } from "react";
import { useBrand } from "@/Providers/Brand";
import { useNavigate } from "react-router-dom";
import { LiveCampaignData } from "../PromoteAd/CampaignInterface/LiveCampaignInterface";
import { useAuth } from "@/Providers/Auth";

type rewardActivity = {
  title: string;
  anchorsCredits: number;
  claimsRemaining: number;
  claimsUsed: number;
  daysIncrease: number;
  description: string;
  isFullyClaimed: boolean;
  maxClaims: number;
};

const guideVideos = [
  {
    id: 1,
    title: "Getting Started with Campaigns",
    duration: "5:30",
    thumbnail: "/placeholder.svg?height=180&width=320",
  },
  {
    id: 2,
    title: "Finding the Right Influencers",
    duration: "4:45",
    thumbnail: "/placeholder.svg?height=180&width=320",
  },
  {
    id: 3,
    title: "Managing Your Campaign",
    duration: "6:15",
    thumbnail: "/placeholder.svg?height=180&width=320",
  },
];

export const DashBoardTasks = () => {
  const authState = useAuth();
  const navigate = useNavigate();
  const brandState = useBrand();

  const [tasks, setTasks] = useState<Record<string, rewardActivity>>({});
  const [sampleCampaign, setSampleCampaign] = useState<LiveCampaignData>();

  const [freeTierDays, setFreeTierDays] = useState(3);
  const [freeTierCredits, setFreeTierCredits] = useState(100);
  const earnedDays = Object.entries(tasks ?? {})
    ?.filter(([, rewardActivity]) => rewardActivity?.claimsUsed > 0)
    ?.reduce(
      (acc, [, rewardActivity]) =>
        acc + rewardActivity?.daysIncrease * rewardActivity?.claimsUsed,
      0
    );
  const potentialDays = Object.entries(tasks ?? {})?.reduce(
    (acc, [, rewardActivity]) =>
      acc + rewardActivity?.daysIncrease * rewardActivity?.maxClaims,
    0
  );
  const totalDaysAvailable = freeTierDays + earnedDays;

  const earnedCredits = Object.entries(tasks ?? {})
    ?.filter(([, rewardActivity]) => rewardActivity?.claimsUsed > 0)
    ?.reduce(
      (acc, [, rewardActivity]) =>
        acc + rewardActivity?.anchorsCredits * rewardActivity?.claimsUsed,
      0
    );
  const potentialCredits = Object.entries(tasks ?? {})?.reduce(
    (acc, [, rewardActivity]) =>
      acc + rewardActivity?.anchorsCredits * rewardActivity?.maxClaims,
    0
  );
  const totalCreditsAvailable = freeTierCredits + earnedCredits;

  const getBrandRewardActivity = useCallback(async () => {
    const result = await brandState?.getBrandRewardActivity();
    if (result?.success) {
      setTasks(result?.availableActivities);
      setFreeTierDays(result?.freeTrial?.days);
      setFreeTierCredits(result?.freeTrial?.credits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getSampleCampaign = useCallback(async () => {
    const result = await brandState?.getSampleCampaign();
    if (result?.success) {
      setSampleCampaign(result?.collab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getBrandRewardActivity();
    getSampleCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {
        <div className="space-y-6">
          {authState?.loggedBrand?.planName === "Free Trial" && (
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Header Section */}
              <div className="space-y-1">
                <h1 className="text-xl font-semibold text-gray-800">
                  Let’s Get You Started!
                </h1>
                <p className="text-sm text-gray-500">
                  Follow these steps to set up your campaign, and enjoy extra
                  trial days and credits along the way.
                </p>
              </div>

              {/* Tasks Section with Accordion */}
              <Accordion
                type="single"
                collapsible
                defaultValue="tasks"
                className="space-y-4"
              >
                <AccordionItem value="tasks" className="border-none">
                  <Card>
                    <CardHeader>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Star className="h-5 w-5 text-yellow-500" />
                          <CardTitle className="text-sm font-semibold">
                            Complete Steps, Earn Rewards
                          </CardTitle>
                        </div>
                      </AccordionTrigger>
                      <CardDescription className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-yellow-500" />
                        Finish key steps to unlock up to {potentialDays} days of
                        Free Access and additional
                        {potentialCredits} redeemable credits.
                      </CardDescription>
                    </CardHeader>
                    <AccordionContent>
                      <CardContent>
                        <div className="space-y-4">
                          {Object.entries(tasks ?? {})?.map(
                            ([task, rewardActivity]) => (
                              <div
                                key={task}
                                className={`flex items-center justify-between p-4 rounded-lg border ${
                                  rewardActivity?.isFullyClaimed
                                    ? "bg-green-50 border-green-200"
                                    : // : task.id <
                                      //   Math.min(
                                      //     ...tasks
                                      //       .filter((t) => !t.completed)
                                      //       .map((t) => t.id)
                                      //   )
                                      // ? "bg-gray-50 border-gray-200 opacity-75"
                                      "bg-white hover:bg-gray-50 transition-colors"
                                }`}
                              >
                                <div className="flex items-center gap-4">
                                  <div
                                    className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                      rewardActivity?.isFullyClaimed
                                        ? "bg-green-100"
                                        : "bg-gray-100"
                                    }`}
                                  >
                                    <CheckCircle2
                                      className={`h-5 w-5 ${
                                        rewardActivity?.isFullyClaimed
                                          ? "text-green-600"
                                          : // : task.id <
                                            //   Math.min(
                                            //     ...tasks
                                            //       .filter((t) => !t.completed)
                                            //       .map((t) => t.id)
                                            //   )
                                            // ? "text-gray-400"
                                            "text-gray-400"
                                      }`}
                                    />
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-medium text-gray-800">
                                      {rewardActivity?.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {rewardActivity?.description}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex flex-col items-end gap-2">
                                    <Badge
                                      variant="secondary"
                                      className="text-blue-600 bg-blue-100 flex items-center gap-1"
                                    >
                                      <Calendar className="h-3 w-3" />+
                                      {rewardActivity?.daysIncrease} Days
                                    </Badge>
                                    <Badge
                                      variant="secondary"
                                      className="text-amber-600 bg-amber-100 flex items-center gap-1"
                                    >
                                      <Coins className="h-3 w-3" />+
                                      {rewardActivity?.anchorsCredits} Credits
                                    </Badge>
                                  </div>
                                  {rewardActivity?.isFullyClaimed ? (
                                    <Badge
                                      variant="secondary"
                                      className="text-green-600 bg-green-100"
                                    >
                                      Completed
                                    </Badge>
                                  ) : (
                                    <Button
                                      onClick={() => {
                                        navigate(
                                          "/Brand/DashBoard/AllCampaign"
                                        );
                                      }}
                                      variant="outline"
                                      size="sm"
                                    >
                                      Start Now
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </CardContent>
                    </AccordionContent>
                  </Card>
                </AccordionItem>
              </Accordion>

              {/* Days Available Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      Your Progress Overview
                    </CardTitle>
                    <CardDescription>
                      Track your earned and potential days of free access and
                      credits
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-600">
                            Campaign Days
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-gray-50">
                              Free Tier: {freeTierDays} days
                            </Badge>
                            <Badge
                              variant="outline"
                              className="bg-green-50 text-green-600"
                            >
                              Earned: +{earnedDays} days
                            </Badge>
                          </div>
                        </div>
                        <Progress
                          value={
                            (totalDaysAvailable /
                              (freeTierDays + potentialDays)) *
                            100
                          }
                          className="h-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Current: {totalDaysAvailable} days</span>
                          <span>
                            Potential: +{potentialDays - earnedDays} days
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-600">
                            anchors Credits
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-gray-50">
                              Free Tier: {freeTierCredits} credits
                            </Badge>
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-600"
                            >
                              Earned: +{earnedCredits} credits
                            </Badge>
                          </div>
                        </div>
                        <Progress
                          value={
                            (totalCreditsAvailable /
                              (freeTierCredits + potentialCredits)) *
                            100
                          }
                          className="h-2 bg-amber-100"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Current: {totalCreditsAvailable} credits</span>
                          <span>
                            Potential: +{potentialCredits - earnedCredits}{" "}
                            credits
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      Your Progress & Rewards
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Tasks Completed
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xl font-bold text-gray-900">
                              {
                                Object.entries(tasks)?.filter(
                                  ([, rewardActivity]) =>
                                    rewardActivity?.isFullyClaimed
                                )?.length
                              }
                              /{Object.entries(tasks)?.length}
                            </p>
                            <span className="text-sm text-gray-500">
                              Steps Done
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <Gift className="h-6 w-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Rewards Available
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xl font-bold text-gray-900">
                              {potentialDays - earnedDays}
                            </p>
                            <span className="text-sm text-gray-500">
                              More Days to Earn
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          {/* Guide Videos Section */}
          <Card className="hidden">
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Video className="h-5 w-5 text-purple-500" />
                Guide Videos
              </CardTitle>
              <CardDescription>
                Watch these videos to learn more about creating successful
                campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {guideVideos.map((video) => (
                  <div key={video.id} className="group relative">
                    <div className="aspect-video relative rounded-lg overflow-hidden">
                      <img
                        src={video.thumbnail || "/placeholder.svg"}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <PlayCircle className="h-12 w-12 text-white" />
                      </div>
                    </div>
                    <div className="mt-2">
                      <h3 className="text-sm font-medium text-gray-800">
                        {video.title}
                      </h3>
                      <p className="text-sm text-gray-600">{video.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Sample Campaign Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-500" />
                Experience a Live Campaign
              </CardTitle>
              <CardDescription>
                See campaign tracking, insights & reports in action—before you
                launch yours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative overflow-hidden rounded-lg border bg-white p-6">
                <div className="absolute top-4 right-4">
                  <Button
                    onClick={() => navigate(`/Brand/DashBoard/AllCampaign`)}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Try It for Yourself
                  </Button>
                </div>
                <div className="max-w-2xl">
                  <h3 className="text-base font-semibold text-gray-800">
                    {sampleCampaign?.campaignInfo?.campaignName}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {sampleCampaign?.campaignInfo?.campaignDescription}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Badge variant="secondary">Demo Campaign</Badge>
                    <Badge variant="secondary">See Real Insights</Badge>
                    <Badge variant="secondary">Mock Influencer Data</Badge>
                  </div>
                  <Button
                    onClick={() =>
                      navigate(
                        `/Brand/DashBoard/AllCampaign/Campaign/${sampleCampaign?._id}/?sampleId=${sampleCampaign?.brandID}`
                      )
                    }
                    className="mt-4"
                    variant="default"
                  >
                    Explore Sample Campaign
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </>
  );
};
